import { createApi } from '@reduxjs/toolkit/query/react';
import type { FetchArgs } from '@reduxjs/toolkit/query';
import { ReauthBaseQuery } from '@skynet/data-auth-api';
import { assistantApiBaseQuery } from '@skynet/data-assistant-api';

/**
 * Telegram Groups mutations enqueue an async job and resolve with `202` +
 * `{ jobId }` almost immediately (SKY-4445) — the submit round-trip itself
 * should never take long, regardless of how long the job it enqueues runs.
 * `assistantApiBaseQuery` sets no request timeout, and RTK Query mutations
 * are never aborted on unmount (only queries are), so a hung connection —
 * the gateway accepting the request but never responding, observed in
 * practice as intermittent worker-side "Bad Gateway" errors — would
 * otherwise leave the submitting button's `isLoading`, and the row/dialog it
 * drives, stuck forever with no client-side recourse.
 */
export const TELEGRAM_GROUPS_SUBMIT_TIMEOUT_MS = 20_000;

/** Normalizes `args` to `FetchArgs` and defaults `timeout` unless the call site already set its own. */
export const withTimeoutDefault = (args: string | FetchArgs, timeoutMs: number): FetchArgs =>
  typeof args === 'string' ? { url: args, timeout: timeoutMs } : { timeout: timeoutMs, ...args };

/**
 * Defaults every request through `baseQuery` to `timeoutMs` unless the call
 * site already set its own `timeout`. Scoped to this package's own base
 * query rather than the shared `assistantApiBaseQuery` (also used by
 * bot-commands, marketing-campaigns, regions, exchanges, bug-reports, and
 * bot-messages) so this doesn't change behavior for those unrelated domains.
 */
export const withSubmitTimeout = (
  baseQuery: typeof assistantApiBaseQuery,
  timeoutMs: number,
): typeof assistantApiBaseQuery => {
  return (args, api, extraOptions) =>
    baseQuery(withTimeoutDefault(args, timeoutMs), api, extraOptions);
};

// Empty API to use as a base for code generation.
// Uses the assistant base query with automatic token refresh, bounded by a
// client-side submit timeout (see `withSubmitTimeout` above).
export const baseApi = createApi({
  reducerPath: 'telegramGroupsApi',
  baseQuery: ReauthBaseQuery(
    withSubmitTimeout(assistantApiBaseQuery, TELEGRAM_GROUPS_SUBMIT_TIMEOUT_MS),
  ),
  endpoints: () => ({}),
});
